/**
 * Error Types - Client-facing error types for API responses
 *
 * These types are shared between backend and frontend apps.
 * Use these to parse and handle API error responses.
 */

import { ComponentCode, ComponentName } from './components';

// Re-export for convenience
export { ComponentCode, ComponentName };

// ============================================================================
// Log Levels
// ============================================================================

/**
 * Log levels - Higher number = less severe, more verbose
 */
export enum LogLevel {
    ERROR = 1,   // Errors only
    WARN = 2,    // Warnings and above
    INFO1 = 3,   // Important info (default for production)
    INFO2 = 4,   // Standard info
    INFO3 = 5,   // Verbose info
    DEBUG = 6    // Debug (most verbose)
}

/**
 * Log level display names
 */
export const LogLevelName: Record<LogLevel, string> = {
    [LogLevel.ERROR]: 'Error',
    [LogLevel.WARN]: 'Warning',
    [LogLevel.INFO1]: 'Info',
    [LogLevel.INFO2]: 'Info 2',
    [LogLevel.INFO3]: 'Info 3',
    [LogLevel.DEBUG]: 'Debug'
};

/**
 * Parse log level from string (for config)
 */
export function parseLogLevel(value: string): LogLevel {
    const upper = value.toUpperCase();
    if (upper === 'ERROR') return LogLevel.ERROR;
    if (upper === 'WARN' || upper === 'WARNING') return LogLevel.WARN;
    if (upper === 'INFO' || upper === 'INFO1') return LogLevel.INFO1;
    if (upper === 'INFO2') return LogLevel.INFO2;
    if (upper === 'INFO3') return LogLevel.INFO3;
    if (upper === 'DEBUG') return LogLevel.DEBUG;
    return LogLevel.INFO1; // Default
}

// ============================================================================
// Error Classification Enums
// ============================================================================

/**
 * Error criticality - indicates how severe the error is
 */
export enum ErrorCriticality {
    FATAL = 1,       // Cannot recover, needs intervention
    RETRIABLE = 2,   // Can retry after some time
    INFO = 3         // Informational, expected error (default)
}

/**
 * Error impact scope - indicates how many users are affected
 */
export enum ErrorImpactScope {
    ALL_USERS = 1,   // System-wide issue
    USER = 2,        // Affects one user across sessions
    SESSION = 3,     // Affects current session only
    OPERATION = 4    // Affects only current operation (default)
}

/**
 * Display names for criticality levels
 */
export const CriticalityName: Record<ErrorCriticality, string> = {
    [ErrorCriticality.FATAL]: 'Fatal',
    [ErrorCriticality.RETRIABLE]: 'Retriable',
    [ErrorCriticality.INFO]: 'Info'
};

/**
 * Display names for impact scope
 */
export const ImpactScopeName: Record<ErrorImpactScope, string> = {
    [ErrorImpactScope.ALL_USERS]: 'All Users',
    [ErrorImpactScope.USER]: 'User',
    [ErrorImpactScope.SESSION]: 'Session',
    [ErrorImpactScope.OPERATION]: 'Operation'
};

// ============================================================================
// Error Detail - The structure returned in API error responses
// ============================================================================

/**
 * ErrorDetail - Structured error information returned by the API
 *
 * The detailCode is an 8-digit number with format CCXSEEEE:
 * - CC (1-2): Component code (01=Handler, 02=Auth, 03=Validation, etc.)
 * - X (3): Criticality (1=Fatal, 2=Retriable, 3=Info)
 * - S (4): Impact scope (1=AllUsers, 2=User, 3=Session, 4=Operation)
 * - EEEE (5-8): Error number (0001-9999)
 */
export interface ErrorDetail {
    /** HTTP status code (e.g., 401, 404, 500) */
    httpStatus: number;

    /** 8-digit structured error code (e.g., 02340001) */
    detailCode: number;

    /** Short identifier (e.g., "INVALID_CREDENTIALS") */
    detailWord: string;

    /** User-friendly error message */
    detailString: string;

    /** Field that caused the error (for validation errors) */
    field?: string;

    /** Additional context (when includeTechnicalDetails enabled) */
    context?: Record<string, unknown>;

    /** Stack trace (when includeStackInResponse enabled) */
    stack?: string;
}

// ============================================================================
// Error Helper Functions
// ============================================================================

/**
 * Get the criticality level from an error
 */
export function getErrorCriticality(error: ErrorDetail): ErrorCriticality {
    return Math.floor((error.detailCode % 1000000) / 100000) as ErrorCriticality;
}

/**
 * Check if an error can be retried
 */
export function isErrorRetriable(error: ErrorDetail): boolean {
    return getErrorCriticality(error) === ErrorCriticality.RETRIABLE;
}

/**
 * Check if an error is fatal (needs intervention)
 */
export function isErrorFatal(error: ErrorDetail): boolean {
    return getErrorCriticality(error) === ErrorCriticality.FATAL;
}

/**
 * Get the component code that generated the error
 */
export function getErrorComponent(error: ErrorDetail): ComponentCode {
    return Math.floor(error.detailCode / 1000000) as ComponentCode;
}

/**
 * Get the error impact scope
 */
export function getErrorScope(error: ErrorDetail): ErrorImpactScope {
    return Math.floor((error.detailCode % 100000) / 10000) as ErrorImpactScope;
}

/**
 * Parsed error code with typed enum values
 */
export interface ParsedErrorCode {
    component: ComponentCode;
    criticality: ErrorCriticality;
    scope: ErrorImpactScope;
    errorNumber: number;
}

/**
 * Parse all components from an error detail code
 */
export function parseErrorCode(code: number): ParsedErrorCode {
    return {
        component: Math.floor(code / 1000000) as ComponentCode,
        criticality: Math.floor((code % 1000000) / 100000) as ErrorCriticality,
        scope: Math.floor((code % 100000) / 10000) as ErrorImpactScope,
        errorNumber: code % 10000
    };
}

// ============================================================================
// API Response Types
// ============================================================================

/**
 * Standard API response wrapper
 * All API endpoints return this structure
 */
/**
 * Generic server→client message, piggybacked on API responses.
 * Discriminated by `type` (e.g. 'notification' | 'duel_request' | 'presence').
 * See docs/FeatureDesigns/duels-invite-smoothing-plan.md.
 */
export interface ServerMessage<P = unknown> {
    id: string;        // dedupe/ack key (delivery id, or `${type}:…`)
    type: string;
    payload: P;
    ts: string;        // ISO
}

export interface ApiResponse<T> {
    success: boolean;
    data?: T;
    /** Error can be string (legacy) or ErrorDetail (new) */
    error?: string | ErrorDetail;
    /** Request correlation ID for tracing */
    requestId?: string;
    /** Generic server→client message bus (piggyback). */
    meta?: { messages?: ServerMessage[] };
}

/**
 * Type guard to check if error is ErrorDetail (new format)
 */
export function isErrorDetail(error: string | ErrorDetail | undefined): error is ErrorDetail {
    return typeof error === 'object' && error !== null && 'detailCode' in error;
}

/**
 * Get error message from ApiResponse (handles both formats)
 */
export function getErrorMessage(response: ApiResponse<unknown>): string {
    if (!response.error) return 'Unknown error';
    if (typeof response.error === 'string') return response.error;
    return response.error.detailString;
}

/**
 * Get ErrorDetail from ApiResponse (creates one from string if needed)
 */
export function getErrorDetail(response: ApiResponse<unknown>): ErrorDetail | undefined {
    if (!response.error) return undefined;
    if (typeof response.error === 'string') {
        // Legacy format - create a minimal ErrorDetail
        return {
            httpStatus: 500,
            detailCode: 0,
            detailWord: 'LEGACY_ERROR',
            detailString: response.error
        };
    }
    return response.error;
}
